import React, { useEffect, useCallback } from 'react';
import { Box, TextField, Tooltip } from '@mui/material';

const EditableCell = ({ 
  config,
  item, 
  field, 
  value, 
  onEdit, 
  isEditing,
  isSelectOpen,
  onSelectOpen,
  onSelectClose,
  onStartEdit,
  onStopEdit,
  fieldContext
}) => {


  // Auto-open selector when editing starts
  useEffect(() => {
    if (isEditing && config.fields[field]?.type === 'selector') {
      const timer = setTimeout(() => {
        onSelectOpen?.();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isEditing, field, config.fields, onSelectOpen]);

  const fieldConfig = config.fields[field];
  const fieldError = item.validationErrors?.find(err => err.field === field);
  const hasError = !!fieldError;
  
  // Check if field is editable
  const isEditable = item.editable !== false;
  
  // Handle conditional fields - generic approach
  const conditionalResult = fieldConfig?.conditional ? fieldConfig.conditional(item, fieldContext) : true;
  const cellEditable = isEditable && conditionalResult;

  // Generic callbacks for selector fields
  const createSelectorCallback = useCallback((callbackName) => {
    const callback = fieldConfig?.[callbackName];
    if (!callback) return undefined;
    
    return (...args) => {
      // Call the callback with a consistent context
      callback(...args, {
        fieldContext,
        getFieldValue: (fieldName) => item[fieldName],
        updateField: (fieldName, value) => onEdit(item.id, fieldName, value),
        currentItem: item
      });
    };
  }, [fieldConfig, fieldContext, item, onEdit])

  const handleCellClick = () => {
    
    if (cellEditable && !isEditing) {
      onStartEdit(item.id, field);
    }

  };

  const handleCellBlur = (e) => {
    // Don't exit edit mode if a select dropdown is open
    const relatedTarget = e?.relatedTarget;
    const isSelectClick = relatedTarget && (
      relatedTarget.getAttribute('role') === 'option' ||
      relatedTarget.classList.contains('MuiMenuItem-root') ||
      relatedTarget.closest('.MuiMenu-paper')
    );
    
    // Don't exit edit mode if clicking inside a modal
    const isModalClick = relatedTarget && (
      relatedTarget.closest('.MuiModal-root') ||
      relatedTarget.closest('.MuiDialog-root') ||
      relatedTarget.closest('[role="dialog"]') ||
      relatedTarget.closest('.MuiBackdrop-root')
    );
    
    // Check if any modal is currently open in the DOM
    const hasOpenModal = document.querySelector('.MuiModal-root:not([aria-hidden="true"])') ||
                        document.querySelector('.MuiDialog-root:not([aria-hidden="true"])') ||
                        document.querySelector('[role="dialog"]:not([aria-hidden="true"])');
    
    if (!isSelectOpen && !isSelectClick && !isModalClick && !hasOpenModal) {
      onStopEdit?.();
    }
  };

  const renderEditingField = () => {
    if (fieldConfig?.type === 'selector' && fieldConfig.component) {
      const SelectorComponent = fieldConfig.component;
      
      return (
        <Box sx={{ width: '100%', height: '100%' }}>
          <SelectorComponent
            value={value === null ? null : (value || '')}
            onChange={(newValue) => {
              onEdit(item.id, field, newValue === null ? null : (newValue || ''));
              
              // Handle field-specific onChange callback if defined
              if (fieldConfig.onValueChange) {
                fieldConfig.onValueChange(newValue, {
                  ...fieldContext,
                  getFieldValue: (fieldName) => fieldName === field ? newValue : item[fieldName],
                  updateField: (fieldName, value) => onEdit(item.id, fieldName, value),
                  currentItem: item
                });
              }
            }}
            // Pass through any selector-specific callbacks defined in config
            {...Object.keys(fieldConfig)
              .filter(key => key.startsWith('on') && typeof fieldConfig[key] === 'function')
              .reduce((acc, key) => {
                // Skip our generic callbacks
                if (key === 'onValueChange' || key === 'onChange') return acc;
                // Create wrapped callback
                acc[key] = createSelectorCallback(key);
                return acc;
              }, {})}
            label=""
            required={false}
            fullWidth
            autoFocus={true}
            open={isSelectOpen}
            returnValue={fieldConfig.returnType}
            onOpen={onSelectOpen}
            onClose={onSelectClose}
            // Handle field dependencies (like contractCompany depending on deliveryCenter)
            {...(fieldConfig.dependsOn && { 
              [fieldConfig.dependencyPropName || fieldConfig.dependsOn]: item[fieldConfig.dependsOn] 
            })}
            // Additional props from config
            {...(fieldConfig.props || {})}
            // Pass item if requested in props
            {...(fieldConfig.props?.item && { item })}
            // Dynamic props based on context (e.g., refreshTrigger)
            {...(fieldConfig.getProps ? fieldConfig.getProps({ fieldContext, currentItem: item }) : {})}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '36px',
                fontSize: '14px',
                '& fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: '1px solid #2563eb' }
              },
              '& .MuiInputLabel-root': { display: 'none' }
            }}
          />
        </Box>
      );
    }

    // Default text field for non-selector fields
    return (
      <TextField
        value={value || ''}
        onChange={(e) => onEdit(item.id, field, e.target.value)}
        onBlur={handleCellBlur}
        autoFocus
        fullWidth
        size="small"
        type={fieldConfig?.type === 'date' ? 'date' : 'text'}
        sx={{
          '& .MuiOutlinedInput-root': {
            height: '36px',
            fontSize: '14px',
            '& fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: '1px solid #2563eb' }
          }
        }}
      />
    );
  };

  const renderDisplayValue = () => {
    // Handle conditional fields that shouldn't be displayed
    if (fieldConfig?.conditional && !fieldConfig.conditional(item, fieldContext)) {
      // Check for custom display when condition is false
      if (fieldConfig.renderWhenHidden) {
        return fieldConfig.renderWhenHidden(item, fieldContext);
      }
      
      // Default display for hidden conditional fields
      return (
        <Box sx={{ 
          color: '#D1D5DB',
          fontStyle: 'italic',
          fontSize: '12px',
          cursor: 'not-allowed',
          userSelect: 'none',
          padding: '8px'
        }}>
          {fieldConfig.hiddenText || 'N/A'}
        </Box>
      );
    }

    const content = (
      <Box
        onClick={handleCellClick}
        sx={{
          color: hasError ? '#EF4444' : 'inherit',
          fontWeight: hasError ? 'bold' : 'normal',
          cursor: cellEditable ? 'pointer' : 'default',
          padding: '8px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          '&:hover': cellEditable ? { backgroundColor: '#F3F4F6' } : {}
        }}
      >
        {fieldConfig?.formatDisplayValue ? fieldConfig.formatDisplayValue(value, fieldContext) : (value || '')}
      </Box>
    );

    return hasError ? (
      <Tooltip title={fieldError.message}>
        {content}
      </Tooltip>
    ) : content;
  };

  if (isEditing && cellEditable) {
    return renderEditingField();
  }

  return renderDisplayValue();
};

export default EditableCell; 